import React from "react"
import { useSelector, useDispatch } from "react-redux"
import { Link, useLocation } from "react-router-dom"
import PlusIcon from "@mui/icons-material/Add"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { purple } from "@mui/material/colors"
import { doEnforceHomePickupCredit } from "@coast/utils"

import { resetSearch, startNewSearch } from "../../../actions/searchActions"

import { FEATURE_FLAGS, phoneNumber } from "../../../config"
import makeSlug from "../../../utils/makeSlug"
import Loader from "./Loader"
import ButtonWithProgress from "../ButtonWithProgress"
import DateTimesList from "./DateTimesList"
import LessonTypeSummary from "./LessonTypeSummary"
import BuyNowScheduleLater from "./BuyNowScheduleLater"
import { usePicker } from "./usePicker"
import { RootState } from "../../../store/configureStore"
import { LessonTypeName, SlotToPick } from "../../../types"
import PickupLocationPicker from "./PickupLocationPicker"
import IllinoisCallToBookDialog from "./IllinoisCallToBookDialog"
import PreviousInstructorSelect from "./PreviousInstructorSelect"
import { useStudentMinutes } from "../../../hooks/useStudentMinutes"
import { getUserLessons } from "../../../actions/userActions"
import { LessonNotificationsRegistrationDialog } from "../lessonNotifications/RegistrationDialog"

interface Props {
  instructorId?: string
}
const IDLE_TIMEOUT = 1000 * 60 * 10 // 10 minutes
const ONLY_MEETING_POINT_LESSON_TYPES = [
  LessonTypeName.Shared,
  LessonTypeName.RoadTest,
  LessonTypeName.RoadTest15,
  LessonTypeName.RoadTestWithCar,
  LessonTypeName.RoadTest15WithCar,
  LessonTypeName.PrepTest60,
]

const PickerContent = ({ instructorId }: Props) => {
  const {
    firebase: { profile, auth },
    location: { searchCity, stateConfig, searchState, searchZipcode },
    search: { errors, slots, endDate, isSearching, selectedLessonType },
    booking: { adminStudent },
  } = useSelector((state: RootState) => state)
  const picker = usePicker(instructorId)
  const theme = useTheme()
  const location = useLocation()
  const studentMinutes = useStudentMinutes(auth.uid)
  const [useHomePickup, setUseHomePickup] = React.useState(false)
  const [showLessonNotifications, setShowLessonNotifications] =
    React.useState(false)
  const [isSlotsExpiredModalOpen, setIsSlotsExpiredModalOpen] =
    React.useState(false)

  const dispatch = useDispatch()

  const isInstructorPage = location.pathname.includes("/instructor/")
  // Note that down in the PickupLocationPicker, we may disable
  // home pickup. This is about whether they're eligible at all.
  const isHomePickupEligible =
    !doEnforceHomePickupCredit(searchState) ||
    !studentMinutes?.drivingMinutesRemaining ||
    studentMinutes?.homePickupsRemaining > 0
  const doShowLocationPicker =
    stateConfig?.hasHomePickup &&
    stateConfig?.hasMeetingPoints &&
    isHomePickupEligible

  const isRoadTest = selectedLessonType.name.includes("road-test")
  const type = isRoadTest ? "road test" : "lesson"

  React.useEffect(() => {
    const userId = profile.isAdmin ? adminStudent?.id : auth?.uid

    // Used to get the most recent instructor.
    dispatch(getUserLessons(userId))
  }, [profile.isAdmin, adminStudent, auth?.uid, dispatch])

  React.useEffect(() => {
    let idleTimer: NodeJS.Timeout | null = null
    // when done searching slots and when expired modal is closed start timing.
    if (!isSlotsExpiredModalOpen && !isSearching) {
      idleTimer = setTimeout(() => {
        setIsSlotsExpiredModalOpen(true)
      }, IDLE_TIMEOUT)
    }
    return () => {
      if (idleTimer) {
        clearTimeout(idleTimer)
      }
    }
  }, [isSlotsExpiredModalOpen, isSearching])

  React.useEffect(() => {
    // Default to home if they have purchased home pickup credits
    if ((studentMinutes?.homePickupsRemaining || 0) > 0) {
      setUseHomePickup(true)
    }
  }, [studentMinutes?.homePickupsRemaining])

  const handleReloadSlots = () => {
    setIsSlotsExpiredModalOpen(false)
    dispatch(resetSearch())
    dispatch(startNewSearch(selectedLessonType.durationMinutes, instructorId))
  }

  const slotFilters = (slot: SlotToPick) => {
    if (instructorId && slot.instructorId !== instructorId) {
      return false
    }

    // This is only for states that have the option of meeting points or home pickup.
    // it will filter in whichever is toggled on
    if (stateConfig?.hasMeetingPoints && stateConfig?.hasHomePickup) {
      if (useHomePickup) {
        return slot.location.meetingPointId === "home"
      }
      return slot.location.meetingPointId !== "home"
    }
    const state = stateConfig?.shortName.toLowerCase()

    if (
      slot.isUnpaired &&
      !FEATURE_FLAGS.allowSharedLessonsSelfBooking.isEnabled(state || "")
    ) {
      return false
    }

    return true
  }

  const renderCityNotAvailable = () => (
    <>
      <h3>We&apos;re sorry, we don&apos;t currently serve your address.</h3>
      <h4>
        We are growing fast, and can often customize our availability and
        locations to where you are. Give us a call at {phoneNumber} or tap the
        icon in the bottom right to chat, and we&apos;ll do what we can to help
        you.
      </h4>
    </>
  )

  const renderLoadMoreButton = () => (
    <ButtonWithProgress
      variant="outlined"
      color="primary"
      clickedCallback={picker.handleMoreClicked}
      isLoading={isSearching}
      loadingText={`Searching to ${endDate.format("MMM D")}`}
      style={{
        minWidth: 50,
        margin: 5,
        color: "#ffffff",
        height: 60,
      }}
      data-cy="load-more-button"
      className="load-more-button"
    >
      <PlusIcon />
      Search More
    </ButtonWithProgress>
  )

  const maybeRenderLessonTypeBookingMessage = () => {
    if (selectedLessonType.name === LessonTypeName.DmvTest) {
      return (
        <div
          style={{
            borderLeft: `4px solid ${theme.palette.secondary.main}`,
            padding: "5px 10px",
            marginBottom: 20,
          }}
        >
          Choose a time <em>90 minutes before</em> your scheduled road test so
          we can have time to practice on the way to the DMV. Don&apos;t see a
          suitable time? Give us a call and we&apos;ll try our best to
          accommodate you.
        </div>
      )
    }

    if (selectedLessonType.name === LessonTypeName.Shared) {
      return (
        <div
          style={{
            borderLeft: `4px solid ${purple[500]}`,
            padding: "5px 10px",
            marginBottom: 20,
          }}
        >
          If available and if your schedule allows, choose a time below colored
          purple. This will maximize your chances of pairing with another
          student for observation. Otherwise, we will do our best to pair you
          before your lesson arrives.
        </div>
      )
    }
    return null
  }

  const renderSearching = () => (
    <div style={{ marginTop: 50, marginBottom: 50 }}>
      {maybeRenderLessonTypeBookingMessage()}
      <Loader
        endDate={picker.maxDaysOut.format("LL")}
        isInstructorSearch={!!instructorId}
      />
    </div>
  )

  const renderNoSlotsFound = () => {
    // Should just be for a moment.
    if (!searchCity) return null

    if (instructorId) {
      return (
        <>
          <h3>
            It looks like this instructor is currently all booked in{" "}
            {searchCity}.
          </h3>
          <h4>
            We likely have other instructors who can fit your needs. Give us a
            call at {phoneNumber}, or{" "}
            <Link
              to={`/${searchState?.toLowerCase()}/location-search/${makeSlug(searchCity)}`}
            >
              {" "}
              go to the location page
            </Link>{" "}
            to see all instructor availability.
          </h4>
        </>
      )
    }
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <h3>
          We&apos;re sorry, it seems we&apos;re all booked up for {type}s in{" "}
          {searchCity}.
        </h3>
        {FEATURE_FLAGS.allowLessonNotifications.isEnabled(auth.uid) ? (
          <>
            <p>
              Register to get notified when more {type} times become available.
            </p>
            <Button
              variant="contained"
              onClick={() => setShowLessonNotifications(true)}
            >
              Register for notifications
            </Button>
          </>
        ) : null}
      </Box>
    )
  }

  const renderDMVTestMessage = () => (
    <div style={{ margin: 20 }}>
      <h3 style={{ fontWeight: "bold" }}>
        Ready to let us help you pass your test?
      </h3>
      <p>
        Call our friendly and helpful support team at {phoneNumber} and
        we&apos;ll get you set up.
      </p>
    </div>
  )

  const maybeRenderCallToBook = () => {
    if (
      profile.isAdmin ||
      searchState?.toLowerCase() !== "il" ||
      FEATURE_FLAGS.allowSharedLessonsSelfBooking.isEnabled(searchState || "")
    )
      return null
    return <IllinoisCallToBookDialog />
  }

  const maybeRenderBuyNow = () => {
    if (!searchZipcode || !selectedLessonType) {
      return null
    }

    return (
      <BuyNowScheduleLater
        lessonTypeName={selectedLessonType.name}
        zipcode={searchZipcode}
      />
    )
  }

  const renderResultsFound = () => {
    const sortedDates = slots ? Object.keys(slots).sort() : null
    return (
      <>
        {doShowLocationPicker ? (
          <PickupLocationPicker
            useHomePickup={useHomePickup}
            setUseHomePickup={setUseHomePickup}
            isHomeEnabled={
              !ONLY_MEETING_POINT_LESSON_TYPES.includes(selectedLessonType.name)
            }
          />
        ) : null}
        <Dialog
          open={isSlotsExpiredModalOpen}
          onClose={() => {
            handleReloadSlots()
          }}
          data-cy="slots-time-out-modal"
        >
          <DialogTitle>Your Search Session Has Expired</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {" "}
              Our availability in your area has changed since you&apos;ve loaded
              this page. We&apos;ll reload the availability for you to select an
              available time.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                handleReloadSlots()
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
        {auth.uid && !isInstructorPage ? <PreviousInstructorSelect /> : null}
        <div>
          <h3 style={{ lineHeight: "34px", fontWeight: 600 }}>
            Choose a {type} time
            {auth.uid && isInstructorPage
              ? " with this instructor"
              : " with an available instructor"}
            :
          </h3>
          <p>
            The availability shown below is updated in real-time. We encourage
            you to book a time listed below, even if it means booking a few
            weeks in advance.
          </p>
          {maybeRenderLessonTypeBookingMessage()}
          {isSearching && (
            <div style={{ marginBottom: 10 }}>
              <em>
                We&apos;re searching additional days, they will appear as
                availability is found...
              </em>
            </div>
          )}
          {FEATURE_FLAGS.allowLessonNotifications.isEnabled(auth.uid) ? (
            <div
              style={{
                borderLeft: `4px solid ${theme.palette.secondary.main}`,
                padding: "5px 20px 25px",
                marginBottom: 20,
              }}
            >
              <h3>Don&apos;t see a {type} time below that works for you?</h3>
              <p>
                Register to get notified when more {type} times become
                available.
              </p>
              <Button
                variant="outlined"
                onClick={() => setShowLessonNotifications(true)}
              >
                Register for notifications
              </Button>
            </div>
          ) : null}
        </div>
        <Grid container spacing={2}>
          {sortedDates && slots
            ? sortedDates.map((slotDay) => {
                const filteredSlots = slots[slotDay].filter(slotFilters)

                return filteredSlots.length > 0 ? (
                  <Grid
                    item
                    className="scrolling-date"
                    key={slotDay}
                    xs={12}
                    sm={6}
                    md={3}
                    lg={2}
                  >
                    {stateConfig && (
                      <DateTimesList
                        slots={filteredSlots}
                        showNames={(profile && profile.isAdmin) || false}
                        timeSelectedCallback={picker.handleTimeSelected}
                        lessonType={selectedLessonType}
                        showTimeZones={profile ? profile.isAdmin : false}
                        stateConfig={stateConfig}
                      />
                    )}
                  </Grid>
                ) : null
              })
            : null}
          <div className="scrolling-date">{renderLoadMoreButton()}</div>
        </Grid>
      </>
    )
  }

  // Errors in this context is something that went fatally wrong with fetching
  // from the API in general, rather than something quasi-expected like being
  // out of availability, or not serving the area at all.
  if (errors || slots === undefined) {
    return (
      <Paper
        style={{ padding: 20, minHeight: 400 }}
        data-cy="availability-picker"
      >
        <h2>Something went wrong. :(</h2>
        <p>
          We&apos;re sorry for the inconvenience, but it seems there was a
          technical problem that has occurred with our system. Our engineering
          team just received a notification and will attempt to fix it ASAP.
        </p>
        <p>
          Please reach out to our friendly customer service team by clicking the
          blue icon at the bottom right to chat, or by calling at {phoneNumber}{" "}
          and we&apos;ll get you all set up.
        </p>
      </Paper>
    )
  }

  const contentSwitch = () => {
    if (picker.isCityNotAvailable) {
      return renderCityNotAvailable()
    }
    if (!profile.isAdmin && selectedLessonType.name === "dmv-test") {
      return renderDMVTestMessage()
    }
    // We want to show partial results, but continue showing the "searching"
    // view when we don't have anything to show yet.
    const resultsToShow = slots && Object.keys(slots).length > 0

    // If we're done searching and still have no results, we want to show
    // the "noSlotsFound" view.
    if (!resultsToShow && picker.didSearchedBeyondMax) {
      return renderNoSlotsFound()
    }
    if (!picker.isDoneLoadingBasics || !resultsToShow) {
      return renderSearching()
    }

    // make this content re-render...
    return renderResultsFound()
  }

  return (
    <>
      <LessonNotificationsRegistrationDialog
        isOpen={showLessonNotifications}
        setIsOpen={setShowLessonNotifications}
      />
      <LessonTypeSummary />
      {maybeRenderCallToBook()}
      {maybeRenderBuyNow()}
      {contentSwitch()}
    </>
  )
}

export default PickerContent
